import { isProduction } from 'endpoints.config';
import { PRE_PROD_DOMAIN, isOnDomain, withScheme } from 'helpers/subjectSites';

const RETURN_URL_KEY = 'MFA_RETURN_URL';
const RETURNED_KEY = 'MFA_RETURNED_TO_SITE';
const BOUNCE_WINDOW_MS = 30 * 1000;

export const MFA_CHALLENGE_AREA = 'mfa-challenge';

const LIVE_DOMAINS = [
  'getset4education.co.uk',
  'getset4pe.co.uk',
  'getset4music.co.uk',
];

/** Only GetSet-owned hosts may receive the browser after a challenge; anything else is an open redirect. */
export const isAllowedReturnUrl = (
  url: string,
  production = isProduction
): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(withScheme(url));
    if (parsed.protocol !== 'https:') return false;
    const domains = production
      ? LIVE_DOMAINS
      : [...LIVE_DOMAINS, PRE_PROD_DOMAIN];
    return domains.some((domain) => isOnDomain(parsed.hostname, domain));
  } catch {
    return false;
  }
};

export const storeMfaReturnUrl = (url: string) => {
  if (!isAllowedReturnUrl(url)) return;
  try {
    window.sessionStorage.setItem(RETURN_URL_KEY, withScheme(url));
  } catch {}
};

export const peekMfaReturnUrl = (): string | null => {
  try {
    const url = window.sessionStorage.getItem(RETURN_URL_KEY);
    return url && isAllowedReturnUrl(url) ? url : null;
  } catch {
    return null;
  }
};

export const consumeMfaReturnUrl = (): string | null => {
  try {
    const url = window.sessionStorage.getItem(RETURN_URL_KEY);
    window.sessionStorage.removeItem(RETURN_URL_KEY);
    return url && isAllowedReturnUrl(url) ? url : null;
  } catch {
    return null;
  }
};

type ReturnedToSite = { host: string; at: number };

const hostOf = (url: string) => new URL(url).hostname;

export const noteReturnedToSite = (returnUrl: string, now = Date.now()) => {
  try {
    const sent: ReturnedToSite = { host: hostOf(returnUrl), at: now };
    window.sessionStorage.setItem(RETURNED_KEY, JSON.stringify(sent));
  } catch {}
};

/**
 * A subject site that accepts the user's verification keeps them. Arriving from the same site
 * straight after returning them to it means it was refused, and asking again would loop.
 */
export const wasJustReturnedToSite = (returnUrl: string, now = Date.now()) => {
  try {
    const stored = window.sessionStorage.getItem(RETURNED_KEY);
    if (!stored) return false;
    const sent: ReturnedToSite = JSON.parse(stored);
    return sent.host === hostOf(returnUrl) && now - sent.at < BOUNCE_WINDOW_MS;
  } catch {
    return false;
  }
};
